import * as Api from '@ViewModels';
import { History } from 'history';
import React from 'react';
import { UseFormGetValues, UseFormReset, UseFormSetValue } from 'react-hook-form';
import { toast } from 'sonner';
import { ErrorMessageViewModel } from '../../../../../extViewmodels/ViewModels';
import {
	invalidateInfiniteDonationSurveys,
	invalidateSurveyById,
	useAccountStripeApiPublicApiKeys,
	useCreateDonationSurveyMutation,
	useReorderDonationSurveyFieldsMutation,
	useUpdateDonationSurveyMutation,
} from '../../../../../queries';
import { DonationSurveyFormData } from '../utils';

/**
 * Props for the useDonationSurveyMutations hook
 */
interface UseDonationSurveyMutationsProps {
	/** Impersonation context for API requests when acting as another user */
	impersonationContext?: Api.IImpersonationContext;
	/** Browser history object for navigation after successful operations */
	history: History;
	/** View model for displaying API errors to the user */
	errorMessages: ErrorMessageViewModel;
	/** Function to log API errors for monitoring and debugging */
	logApiError: (message: string, error: Api.IOperationResultNoValue) => void;
	/** React Hook Form function to get current form values */
	getValues: UseFormGetValues<DonationSurveyFormData>;
	/** React Hook Form function to reset form state */
	reset: UseFormReset<DonationSurveyFormData>;
	/** React Hook Form function to set individual field values */
	setValue: UseFormSetValue<DonationSurveyFormData>;
	/** React Hook Form function to watch/subscribe to field changes */
	watch: (field: string) => any;
}

/**
 * Custom hook for managing all API mutations related to donation surveys
 *
 * This hook encapsulates all mutation logic for donation surveys including:
 * - Creating new donation surveys
 * - Updating existing donation surveys
 * - Reordering custom form fields within surveys
 * - Fetching Stripe API keys for payment processing
 *
 * Follows the pattern established in EventRegistrationSurvey and CustomSurvey
 * to maintain architectural consistency across all survey types.
 *
 * @param props - Configuration and dependencies for the mutations
 * @returns Object containing mutation functions and Stripe API keys
 *
 * @example
 * const { createSurveyMutation, updateSurveyMutation, publishableApiKeys } =
 *   useDonationSurveyMutations({
 *     history,
 *     errorMessages,
 *     logApiError,
 *     getValues,
 *     reset,
 *     setValue,
 *     watch
 *   });
 */
export const useDonationSurveyMutations = ({
	impersonationContext,
	history,
	errorMessages,
	logApiError,
	getValues,
	reset,
	setValue,
	watch,
}: UseDonationSurveyMutationsProps) => {
	// Fetch Stripe API public keys needed for payment processing in donation forms
	const stripePublicKeys = useAccountStripeApiPublicApiKeys({
		impersonationContext,
	});

	// Memoize the publishable API keys to prevent unnecessary re-renders
	const publishableApiKeys = React.useMemo(() => {
		return stripePublicKeys?.data ? { publishableKey: stripePublicKeys.data } : undefined;
	}, [stripePublicKeys]);

	/**
	 * Mutation for creating a new donation survey
	 * On success:
	 * - Displays success toast
	 * - Redirects to edit page for the newly created survey
	 * - Invalidates cached survey lists to show the new survey
	 * - Resets form dirty state while preserving values
	 */
	const createSurveyMutation = useCreateDonationSurveyMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			toast.error('Failed to create donation survey. Please try again.');
			logApiError('DonationCreateSurvey-Error', error);
		},
		onSuccess: (val: Api.IDonationSurvey) => {
			toast.success('Donation survey created successfully! You can now share it with donors.');
			// Navigate to the edit page for the newly created survey
			history.replace(`/surveys/donation/edit/${val.id}`);
			// Invalidate React Query caches to refresh survey lists
			invalidateInfiniteDonationSurveys();
			invalidateSurveyById();
			// Reset form dirty state while keeping current values to prevent unsaved changes warning
			const currentFormValues = getValues();
			reset({ ...currentFormValues }, { keepValues: true, keepDefaultValues: false });
		},
	});

	/**
	 * Mutation for updating an existing donation survey
	 * On success:
	 * - Displays success toast
	 * - Invalidates cached survey data to reflect changes
	 * - Reloads frequency settings from server response to reflect backend corrections
	 * - Resets form dirty state
	 */
	const updateSurveyMutation = useUpdateDonationSurveyMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			toast.error('Failed to update donation survey. Please try again.');
			logApiError('DonationUpdateSurvey-Error', error);
		},
		onSuccess: (updatedSurvey?: Api.IDonationSurvey) => {
			toast.success('Donation survey updated successfully! Changes have been saved.');
			// Invalidate React Query caches to refresh survey data
			invalidateInfiniteDonationSurveys();
			invalidateSurveyById();

			// If we have the updated survey from the server, reload the frequency settings
			// This ensures any backend corrections (e.g., auto-enabling one-time when all frequencies disabled)
			// are reflected in the form
			if (updatedSurvey?.donationOptions) {
				const opts = updatedSurvey.donationOptions;

				// Update one-time frequency settings if present
				if (opts.oneTimeDonationSettings) {
					setValue('oneTimeSettings.enabled', opts.oneTimeDonationSettings.enabled);
				}

				// Update monthly frequency settings if present
				if (opts.monthlyDonationSettings) {
					setValue('monthlySettings.enabled', opts.monthlyDonationSettings.enabled);
				}

				// Update annual frequency settings if present
				if (opts.annualDonationSettings) {
					setValue('annualSettings.enabled', opts.annualDonationSettings.enabled);
				}
			}

			// Reset form dirty state while keeping current values to prevent unsaved changes warning
			const currentFormValues = getValues();
			reset({ ...currentFormValues }, { keepValues: true, keepDefaultValues: false });
		},
	});

	/**
	 * Mutation for reordering custom form fields within a donation survey
	 * On success:
	 * - Displays success toast
	 * - Updates form state with reordered fields while preserving complete field data
	 *
	 * Note: The server response only includes minimal field data (id, order),
	 * so we preserve the complete field objects from the current form state
	 * to maintain field options and other configuration.
	 */
	const reorderSurveyFieldsMutation = useReorderDonationSurveyFieldsMutation({
		impersonationContext,
		onError: error => {
			errorMessages.pushApiError(error);
			toast.error('Failed to reorder donation survey fields');
			logApiError('DonationReorderSurveyFields-Error', error);
		},
		onSuccess: data => {
			toast.success('Donation survey fields reordered successfully');
			if (data?.fields) {
				// Get current form fields to preserve their complete data (including options)
				const currentFields = watch('customForm.fields') || [];

				// Create a map of current fields by ID for quick lookup
				// This allows us to preserve complete field data (options, validation, etc.)
				const currentFieldsMap = new Map(currentFields.map((field: Api.IFormField<string>) => [field.id, field]));

				// Reorder the complete field objects based on the server response order
				// Filter out any fields that might not exist in the current form state
				const reorderedCompleteFields = data.fields
					.map(serverField => currentFieldsMap.get(serverField.id))
					.filter(Boolean) as Api.IFormField<string>[];

				// Update the form with the reordered fields
				setValue('customForm.fields', reorderedCompleteFields);
			}
		},
	});

	return {
		publishableApiKeys,
		createSurveyMutation,
		updateSurveyMutation,
		reorderSurveyFieldsMutation,
	};
};
